import { Home } from '@mui/icons-material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import KeyIcon from '@mui/icons-material/Key';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddressBookIcon from 'components/icons/AddressBookIcon';
import AIQEventTriggers from 'components/icons/AIQEventTriggersPageIcon';
import AlisaIcon from 'components/icons/AlisaIcon';
import AppointmentIcon from 'components/icons/AppointmentIcon';
import AudioIcon from 'components/icons/AudioIcon';
import AuthTruckIcon from 'components/icons/AuthTruckIcon';
import BuildingShieldIcon from 'components/icons/BuildingShieldIcon';
import CameraTronIcon from 'components/icons/CameraTronIcon';
import ClientTruckIcon from 'components/icons/ClientTruckIcon';
import ControlIcon from 'components/icons/ControlIcon';
import DiagramIcon from 'components/icons/DiagramIcon';
import DivisionIcon from 'components/icons/DivisionIcon';
import InventoryIcon from 'components/icons/InventoryIcon';
import LassoIcon from 'components/icons/LassoIcon';
import MediaMTXIcon from 'components/icons/MediaMTXIcon';
import MonitorIcon from 'components/icons/MonitorIcon';
import MonitoringObjectIcon from 'components/icons/MonitoringObjectIcon';
import OrganizationIcon from 'components/icons/OrganizationIcon';
import PenIcon from 'components/icons/PenIcon';
import PersonnelIcon from 'components/icons/PersonnelIcon';
import RelayIcon from 'components/icons/RelayIcon';
import RequestTicketsIcon from 'components/icons/RequestTicketsIcon';
import ShieldHalvedIcon from 'components/icons/ShieldHalvedIcon';
import SSOIcon from 'components/icons/SSOIcon';
import Tollbooth from 'components/icons/Tollbooth';
import UserShieldIcon from 'components/icons/UserShieldIcon';
import YardCheckIcon from 'components/icons/YardCheckIcon';
import { FEATURE_NAME, ROLES, ROUTE_PATH } from 'utils/constants';
import { hasAtLeastFullAccessPermissionForFeature, hasAtLeastReadPermissionForFeature } from 'utils/userPermissionHelper';
import { isExternalUser } from 'utils/userRolesHelper';

export const getInventoryRoutes = (featureFlags) => {
    const { isInventoryEnabled, isYardCheckEnabled } = featureFlags;

    if (!isInventoryEnabled && !isYardCheckEnabled) {
        return null;
    }

    const inventoryRoutes = [];

    if (isInventoryEnabled) {
        inventoryRoutes.push({
            Icon: InventoryIcon,
            path: '/inventory-list',
            allowedRoles: [ROLES.ADMIN, ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER],
            text: 'List',
            permissionChecker: (user, features) => hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.INVENTORY, features),
        });
    }

    if (isYardCheckEnabled) {
        inventoryRoutes.push({
            Icon: YardCheckIcon,
            path: '/yard-check-list',
            allowedRoles: [ROLES.ADMIN, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER],
            text: 'Inventory Check',
            permissionChecker: (user, features) => hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.YARD_CHECK, features),
        });
    }

    return {
        text: 'Inventory',
        Icon: InventoryIcon,
        allowedRoles: [ROLES.ADMIN, ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER],
        permissionChecker: (user, features) =>
            hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.INVENTORY, features) ||
            hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.YARD_CHECK, features),
        items: inventoryRoutes,
    };
};

export const getAvailableRoutes = (featureFlags, user) => {
    const groupedRoutes = [];

    const transactionItems = [
        { path: '/', text: 'Home', Icon: Home },
        {
            allowedRoles: [ROLES.ADMIN, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER],
            permissionChecker: (user, features) => hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.ACCESS_POINT_RECORDS, features),
            path: '/access-point-record-list',
            text: 'Access Point Records',
            Icon: Tollbooth,
        },
        getInventoryRoutes(featureFlags),
        {
            allowedRoles: [ROLES.ADMIN, ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER],
            permissionChecker: (user, features) => hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.APPOINTMENTS, features),
            path: '/appointment-list',
            text: 'Appointments',
            Icon: AppointmentIcon,
        },
        {
            text: 'Authorized Lists',
            Icon: ShieldHalvedIcon,
            allowedRoles: [ROLES.ADMIN, ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER],
            permissionChecker: (user, features) =>
                hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.AUTHORIZED_LIST_PEOPLE, features) ||
                hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.AUTHORIZED_LIST_ORGANIZATION, features),
            items: [
                {
                    Icon: UserShieldIcon,
                    path: '/people-authorized-list',
                    text: 'People',
                    allowedRoles: [ROLES.ADMIN, ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER],
                    permissionChecker: (user, features) => hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.AUTHORIZED_LIST_PEOPLE, features),
                },
                {
                    Icon: BuildingShieldIcon,
                    path: '/company-authorized-list',
                    text: 'Organizations',
                    allowedRoles: [ROLES.ADMIN, ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER],
                    permissionChecker: (user, features) =>
                        hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.AUTHORIZED_LIST_ORGANIZATION, features),
                },
                ...(featureFlags.isClientEquipmentEnabled
                    ? [
                          {
                              path: '/location-equipment',
                              text: 'Equipment',
                              Icon: AuthTruckIcon,
                              allowedRoles: [ROLES.ADMIN, ROLES.TEAM_LEADER, ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN],
                              permissionChecker: (user, features) =>
                                  hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.AUTHORIZED_EQUIPMENT, features),
                          },
                      ]
                    : []),
            ],
        },
    ].filter(Boolean);

    if (featureFlags.isClientRequestEnabled) {
        transactionItems.push({
            allowedRoles: [ROLES.ADMIN, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER],
            permissionChecker: (user, features) => hasAtLeastFullAccessPermissionForFeature(user, FEATURE_NAME.CLIENT_REQUEST, features),
            path: '/request-tickets',
            text: 'Request Tickets',
            Icon: RequestTicketsIcon,
        });
    }

    groupedRoutes.push({ section: 'Facility', items: transactionItems });

    const managementSubItems = [
        {
            path: '/organization-list',
            text: 'Organizations',

            allowedRoles: [ROLES.ADMIN, ROLES.USER, ROLES.HQ_SUPPORT, ROLES.SALES, ROLES.TEAM_LEADER],
            Icon: OrganizationIcon,
        },
        {
            path: '/location-list',
            text: 'Locations',
            allowedRoles: [ROLES.ADMIN, ROLES.USER, ROLES.HQ_SUPPORT, ROLES.SALES],
            Icon: LocationOnIcon,
        },
        { path: '/client-personnel', text: 'Personnel', Icon: PersonnelIcon, allowedRoles: [ROLES.ADMIN, ROLES.TEAM_LEADER, ROLES.AGENT] },
    ];

    if (featureFlags.isClientEquipmentEnabled) {
        managementSubItems.push({
            path: '/client-equipment',
            text: 'Equipment',
            Icon: ClientTruckIcon,
            allowedRoles: [ROLES.ADMIN, ROLES.TEAM_LEADER, ROLES.AGENT],
        });
    }

    const managementItems = [
        {
            text: 'Client',
            allowedRoles: [ROLES.ADMIN, ROLES.USER, ROLES.HQ_SUPPORT, ROLES.SALES, ROLES.TEAM_LEADER, ROLES.AGENT],
            Icon: AddressBookIcon,
            items: managementSubItems,
        },
        {
            allowedRoles: [ROLES.ADMIN, ROLES.HR, ROLES.USER],
            path: '/employees',
            text: 'Employees',
            Icon: AssignmentIndIcon,
        },
        { path: '/divison-list', text: 'Divisions', allowedRoles: [ROLES.ADMIN, ROLES.USER, ROLES.HQ_SUPPORT, ROLES.SALES], Icon: DivisionIcon },
    ];

    groupedRoutes.push({
        section: 'Management',
        items: managementItems,
    });

    const technicalItems = [
        {
            path: '/monitoring-object',
            text: 'Monitoring Objects',
            allowedRoles: [ROLES.ADMIN, ROLES.USER, ROLES.TECH],
            Icon: MonitoringObjectIcon,
        },
        {
            text: 'Monitoring Elements',
            allowedRoles: [ROLES.ADMIN, ROLES.USER, ROLES.TECH],
            Icon: MonitorIcon,
            items: [
                { path: '/monitoring-elements', text: 'All Elements', Icon: MonitorIcon },
                { path: '/monitoring-elements/audio', text: 'Audio', Icon: AudioIcon },
                { path: '/monitoring-elements/relay', text: 'Relays', Icon: RelayIcon },
            ],
        },
    ];

    if (!featureFlags.isUserPermissionEnabled) {
        technicalItems.push({
            allowedRoles: [ROLES.ADMIN],
            Icon: KeyIcon,
            path: '/authorized-organization-locations',
            text: 'Authorized Organizations & Locations',
        });
    }

    technicalItems.push({
        allowedRoles: [ROLES.ADMIN],
        text: 'Macaw',
        Icon: AlisaIcon,
        items: [{ path: '/regions-of-interest', text: 'Regions Of Interest', Icon: LassoIcon }],
    });

    if (featureFlags.isDynamicFormsEnabled) {
        technicalItems.push({
            allowedRoles: [ROLES.ADMIN],
            Icon: DynamicFormIcon,
            text: 'Dynamic Forms',
            items: [
                { Icon: DynamicFormIcon, path: '/dynamic-form-list', text: 'Dynamic Forms' },
                { Icon: DiagramIcon, path: '/section-template-list', text: 'Section Templates' },
                { Icon: PenIcon, path: '/fields-template-list', text: 'Fields Templates' },
            ],
        });
    }

    technicalItems.push({
        text: 'Control Center',
        allowedRoles: [ROLES.ADMIN, ROLES.USER, ROLES.TECH],
        Icon: ControlIcon,
        items: [
            { path: '/control-center', text: 'Mission Control', Icon: ControlIcon },
            { path: '/camera-tron', text: 'CameraTron', Icon: CameraTronIcon },
            { path: '/media-mtx', text: 'MediaMTX', Icon: MediaMTXIcon },
            { path: '/incoming-webooks', text: 'Incoming Webhooks', Icon: AIQEventTriggers },
        ],
    });

    groupedRoutes.push({ section: 'Technical', items: technicalItems });

    const accessItems = [];

    if (featureFlags.isUserPermissionEnabled) {
        accessItems.push({
            allowedRoles: [ROLES.ADMIN],
            Icon: KeyIcon,
            permissionChecker: (user, features) => hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.USER_AND_PERMISSION, features),
            path: '/users-permissions',
            text: 'Users and Permissions',
        });
    }

    if (featureFlags.isSsoEnabled) {
        accessItems.push({
            allowedRoles: [ROLES.ADMIN],
            path: ROUTE_PATH.SSO_CONFIGURATION,
            text: 'SSO Configuration',
            Icon: SSOIcon,
        });
    }

    if (accessItems.length > 0) {
        groupedRoutes.push({ section: 'Access', items: accessItems });
    }

    if (isExternalUser(user)) {
        const flatItems = groupedRoutes.flatMap((group) => group.items || []);
        return [{ items: flatItems }];
    }

    return groupedRoutes;
};
